import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import React, { memo } from 'react';
import { FormattedMessage } from 'react-intl';

import ProgressiveImage from '../../../components/Image/ProgressiveImage';
import useSharetribeImage from '../../../util/hooks/useSharetribeImage';
import { chunkGalleryImages } from './galleryUtils';

// Same variant sets SectionImages already requests for listing images.
const FULL_TILE_VARIANTS = [
  'landscape-blur',
  'landscape-crop-x05',
  'landscape-crop1x',
  'landscape-crop2x',
];
const HALF_TILE_VARIANTS = ['landscape-blur', 'landscape-crop-x05', 'landscape-crop1x'];

const contentSx = {
  maxWidth: '760px',
  mx: 'auto',
  px: 2,
  pt: 10,
  pb: 6,
};

// 16:9 tile with the same hover affordance as the hero grid images.
const tileSx = {
  position: 'relative',
  width: '100%',
  borderRadius: '8px',
  overflow: 'hidden',
  padding: 0,
  border: 'none',
  display: 'block',
  backgroundColor: 'transparent',
  '&:before': {
    content: '""',
    display: 'block',
    paddingTop: '56%',
  },
  '&:hover': {
    cursor: 'pointer',
    filter: 'brightness(80%)',
    transition: '0.2s',
  },
};

const tileImageWrapperSx = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
};

const GalleryTile = ({ image, index, title, variants, sizes, onPhotoClick }) => {
  const processed = useSharetribeImage(image, variants);
  return (
    <Box
      component="button"
      data-testid={`gallery-tile-${index}`}
      onClick={() => onPhotoClick(index)}
      sx={tileSx}
      type="button"
    >
      <Box sx={tileImageWrapperSx}>
        <ProgressiveImage
          alt={title}
          blurSrc={processed.blurSrc}
          sizes={sizes}
          src={processed.src}
          srcset={processed.srcset}
        />
      </Box>
    </Box>
  );
};

/**
 * Airbnb-style photo overview: alternating rows of one full-width image and
 * two half-width images (single column on mobile). No grouping by category.
 *
 * @param {object} props
 * @param {Array<object>} props.images - Default (non-surround) listing images
 * @param {(index: number) => void} props.onPhotoClick - Called with the global image index
 * @param {string} props.title - Listing title, used as image alt text
 */
const ListingGallery = ({ images, onPhotoClick, title }) => {
  const rows = chunkGalleryImages(images);

  return (
    <Box sx={contentSx}>
      <Typography component="h2" mb={3} variant="h5">
        <FormattedMessage id="ListingGallery.title" />
      </Typography>
      {rows.map((row, rowIndex) => (
        <Grid container key={rowIndex} mb={1} spacing={1}>
          {row.items.map(({ image, index }) => (
            <Grid key={image.id.uuid} size={{ sm: row.type === 'pair' ? 6 : 12, xs: 12 }}>
              <GalleryTile
                image={image}
                index={index}
                onPhotoClick={onPhotoClick}
                sizes={
                  row.type === 'pair'
                    ? '(max-width: 600px) 100vw, 380px'
                    : '(max-width: 600px) 100vw, 760px'
                }
                title={title}
                variants={row.type === 'pair' ? HALF_TILE_VARIANTS : FULL_TILE_VARIANTS}
              />
            </Grid>
          ))}
        </Grid>
      ))}
    </Box>
  );
};

export default memo(ListingGallery);
